import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { RefreshControl, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import moment from 'moment';
import Toast from 'react-native-root-toast';

import { APPROVE_CHECKLIST_SUBMISSION, GET_CHECKLIST_SUBMISSION } from '../../../common/graphql/checklist';
import {
	CHECKLIST_SIGN_OFF_STATE,
	formatChecklistSignOffTime,
	getChecklistSignOff,
} from '../../../common/utils/checklist/checklistSignOff';
import { useUserContext } from '../../../common/utils/context';
import { getUserName } from '../../../common/utils/user/getUserName';

import { Text } from '../../components';
import { TextInput } from '../../components/input';
import {
	ActionDock,
	Card,
	Screen,
	SectionTitle,
} from '../../design/components';
import { useRefreshPage } from '../../hooks';
import useBackLabel from '../../hooks/useBackLabel';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import ChecklistAnswerSummary from './ChecklistAnswerSummary';
import { createChecklistFormViewStyles } from './ChecklistFormView.styles';

/**
 * Manager sign-off page, opened from the sign-off push notification. Shows the driver's
 * submitted checklist; only the manager the driver selected can sign it off.
 */
const ChecklistSignOffView = ({ route }) => {
	const styles = useThemedStyles(createStyles);
	const formStyles = useThemedStyles(createChecklistFormViewStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const { tenant, user } = useUserContext();
	const backLabel = useBackLabel();
	const { checklistSubmissionId } = route?.params || {};
	const [note, setNote] = useState('');

	// A new notification can reuse this screen; start each checklist with an empty note.
	useEffect(() => {
		setNote('');
	}, [checklistSubmissionId]);

	const { data, loading, error, refetch } = useQuery(GET_CHECKLIST_SUBMISSION, {
		variables: { id: checklistSubmissionId },
		fetchPolicy: 'cache-and-network',
		skip: !checklistSubmissionId,
	});
	const [approveChecklistSubmission, { loading: isSigningOff }] = useMutation(APPROVE_CHECKLIST_SUBMISSION);
	const [refreshPage, isRefreshing] = useRefreshPage(refetch);

	const submission = data?.getChecklistSubmission;
	const signOff = getChecklistSignOff(submission, tenant?.timezone);
	const isSignedOff = signOff?.state === CHECKLIST_SIGN_OFF_STATE.SIGNED_OFF;
	const canSignOff = Boolean(!isSignedOff && submission?.signOffManager && submission.signOffManager.id === user?.id);

	const signOffChecklist = useCallback(async () => {
		try {
			await approveChecklistSubmission({
				variables: {
					id: submission.id,
					note: note.trim() || undefined,
				},
			});
			Toast.show('Signed off');
		}
		catch (signOffError) {
			Toast.show(signOffError?.message || 'Unable to sign off the checklist');
		}
	}, [approveChecklistSubmission, note, submission?.id]);

	const isLoading = loading && !submission;
	let errorMessage;
	if (!isLoading && !submission) {
		errorMessage = error
			? 'The checklist could not be loaded. Check your connection and try again.'
			: 'Checklist not found. It may have been removed, or it belongs to another state.';
	}

	const details = submission ? [
		{ label: 'Driver', value: getUserName(submission.user) },
		{ label: 'Date', value: submission.date ? moment(submission.date, 'YYYY-MM-DD').format('ddd D MMM YYYY') : null },
		{ label: 'Vehicle', value: submission.vehicleDetails?.name },
		{
			label: 'Submitted',
			value: submission.submittedAt ? formatChecklistSignOffTime(tenant?.timezone, submission.submittedAt) : null,
		},
	].filter(detail => detail.value) : [];

	let dockHint;
	if (!isSignedOff && !canSignOff && submission?.signOffManager) {
		dockHint = `Only ${getUserName(submission.signOffManager)} can sign off this checklist.`;
	}

	return (
		<Screen
			backLabel={backLabel}
			heading={submission ? submission.template?.title || 'Checklist' : undefined}
			subheading={submission ? 'Manager sign-off' : undefined}
			isLoading={isLoading}
			error={errorMessage}
			onRetry={() => refetch()}
			keyboardShouldPersistTaps="handled"
			refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={refreshPage} />}
			dock={submission && (canSignOff || dockHint) ? (
				<ActionDock
					hint={dockHint}
					action={canSignOff ? {
						label: 'Sign off',
						onPress: signOffChecklist,
						loading: isSigningOff,
						disabled: isSigningOff,
						icon: 'check',
					} : undefined}
				/>
			) : undefined}
		>
			{
				Boolean(submission) && (
					<>
						<Card>
							{
								details.map(detail => (
									<View key={detail.label} style={styles.detailRow}>
										<Text style={styles.detailLabel}>{detail.label}</Text>
										<Text style={styles.detailValue}>{detail.value}</Text>
									</View>
								))
							}
						</Card>
						{
							Boolean(signOff) && (
								<Card tone={isSignedOff ? 'success' : 'warning'} style={styles.statusCard}>
									<View style={styles.statusIcon}>
										<Feather
											name={isSignedOff ? 'check-circle' : 'clock'}
											size={24}
											color={isSignedOff ? colors.success : colors.warning}
										/>
									</View>
									<View style={styles.statusText}>
										<Text style={styles.statusLabel}>{canSignOff ? 'Waiting for your sign-off' : signOff.text}</Text>
										{Boolean(signOff.note) && <Text style={styles.statusNote}>Note: {signOff.note}</Text>}
									</View>
								</Card>
							)
						}
						<ChecklistAnswerSummary
							items={submission.template?.items}
							answers={submission.answers}
							flaggedItemIds={submission.flaggedItemIds}
						/>
						{
							canSignOff && (
								<View style={formStyles.section}>
									<SectionTitle title="Your note (optional)" />
									<TextInput
										value={note}
										onChange={setNote}
										placeholder="Add a note for the driver"
										multiline={true}
										maxLength={500}
										accessibilityLabel="Note for the driver"
										style={formStyles.commentInput}
									/>
								</View>
							)
						}
					</>
				)
			}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, space, type } }) => ({
	detailRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		gap: space.md,
		paddingVertical: space.xs,
	},
	detailLabel: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
	detailValue: {
		...type.bodySmallSemibold,
		color: colors.text,
		flexShrink: 1,
		textAlign: 'right',
	},
	statusCard: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: space.md,
	},
	// One title line high, so the icon centres on the first line, not on the whole card.
	statusIcon: {
		height: type.rowTitleSemibold.lineHeight,
		justifyContent: 'center',
	},
	statusText: {
		flex: 1,
		gap: space.xs,
	},
	statusLabel: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	statusNote: {
		...type.bodySmall,
		color: colors.text,
	},
});

export default ChecklistSignOffView;
